import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ShieldCheck, Package, Save, Plus, Trash2, BadgeCheck, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ENGINES, MODDING_ENGINES, PROJECT_TYPES, DIFFICULTIES } from "@/content/engines";

const EASE = [0.16, 1, 0.3, 1];
const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";

export default function ContractorTab() {
  const [profile, setProfile] = useState(null);
  const [listings, setListings] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", engine: "fivem", game_title: "", price: "", frameworks: [], difficulty: "Moderate", compat_versions: [], file_url: "", patch_notes: "", video_url: "", test_server: "" });
  const [spec, setSpec] = useState("");

  const load = useCallback(() => {
    api.get("/market/profile").then((r) => setProfile(r.data)).catch(() => {});
    api.get("/market/listings/mine").then((r) => setListings(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!profile) return null;

  const toggle = (key, val) => setProfile((p) => ({ ...p, [key]: p[key].includes(val) ? p[key].filter((x) => x !== val) : [...p[key], val] }));

  const saveProfile = async () => {
    setBusy(true);
    try {
      await api.patch("/market/profile", profile);
      toast.success("Contractor profile updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const createListing = async () => {
    setBusy(true);
    try {
      await api.post("/market/listings", { ...form, price: parseFloat(form.price) });
      toast.success("Listing deployed to the Modding Marketplace");
      setForm({ title: "", description: "", engine: "fivem", game_title: "", price: "", frameworks: [], difficulty: "Moderate", compat_versions: [], file_url: "", patch_notes: "", video_url: "", test_server: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const removeListing = async (id) => {
    try {
      await api.delete(`/market/listings/${id}`);
      toast.success("Listing removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const eng = ENGINES[form.engine];
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleForm = (k, v) => setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));

  return (
    <div data-testid="contractor-tab" className="flex flex-col gap-6 max-w-4xl">
      {profile.id_status !== "verified" && (
        <div className="border border-yellow-400/40 bg-yellow-400/5 p-4 font-mono text-[10px] text-yellow-300 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" /> Identity verification required before you appear in the Service Gallery or can deploy marketplace listings — complete step 1 of Onboarding.
        </div>
      )}

      {/* Gallery profile */}
      <motion.section className="border border-white/10 bg-black/40 p-6 flex flex-col gap-4" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-sans font-bold uppercase text-base text-zinc-50 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-nc-blue" /> Service Gallery Profile</h3>
          <label className="flex items-center gap-2 cursor-pointer">
            <input data-testid="gallery-listed-toggle" type="checkbox" checked={profile.listed} onChange={(e) => setProfile((p) => ({ ...p, listed: e.target.checked }))} className="accent-[#2E6BFF]" />
            <span className="font-mono text-[10px] text-zinc-300 uppercase tracking-[0.15em]">Listed as Active Personnel</span>
          </label>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Tagline</label>
            <input data-testid="gallery-tagline" className={inputCls} placeholder="e.g. FiveM framework specialist" value={profile.tagline} onChange={(e) => setProfile((p) => ({ ...p, tagline: e.target.value }))} />
          </div>
          <div>
            <label className={labelCls}>Rate</label>
            <input data-testid="gallery-rate" className={inputCls} placeholder="e.g. $55/hr or fixed quotes" value={profile.rate} onChange={(e) => setProfile((p) => ({ ...p, rate: e.target.value }))} />
          </div>
        </div>
        <div>
          <label className={labelCls}>Specialties (press Enter to add)</label>
          <div className="flex gap-1.5 flex-wrap mb-2">
            {profile.specialties.map((s) => (
              <span key={s} className="font-mono text-[9px] uppercase border border-nc-blue/40 text-nc-blue px-2 py-1 flex items-center gap-1.5">
                {s} <button onClick={() => setProfile((p) => ({ ...p, specialties: p.specialties.filter((x) => x !== s) }))} className="hover:text-red-400">×</button>
              </span>
            ))}
          </div>
          <input
            data-testid="gallery-specialty-input"
            className={inputCls}
            placeholder="e.g. Lua Scripting, MLO Design, Blueprint Systems"
            value={spec}
            onChange={(e) => setSpec(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && spec.trim()) { setProfile((p) => ({ ...p, specialties: [...p.specialties, spec.trim()] })); setSpec(""); } }}
          />
        </div>
        <div>
          <label className={labelCls}>Engine platforms</label>
          <div className="flex gap-2 flex-wrap">
            {Object.entries(ENGINES).map(([k, v]) => (
              <button key={k} data-testid={`gallery-engine-${k}`} onClick={() => toggle("engines", k)} className={`border px-3 py-2 font-mono text-[9px] tracking-[0.15em] uppercase transition-colors ${profile.engines.includes(k) ? v.accent : "border-white/10 text-zinc-400 hover:border-white/30"}`}>{v.short}</button>
            ))}
          </div>
        </div>
        <div>
          <label className={labelCls}>Project types</label>
          <div className="flex gap-2 flex-wrap">
            {PROJECT_TYPES.map((t) => (
              <button key={t} data-testid={`gallery-type-${t.replace(/[^A-Za-z]/g, "")}`} onClick={() => toggle("project_types", t)} className={`border px-3 py-2 font-mono text-[9px] tracking-[0.15em] uppercase transition-colors ${profile.project_types.includes(t) ? "text-nc-orange border-nc-orange/50 bg-nc-orange/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>{t}</button>
            ))}
          </div>
        </div>
        <button data-testid="gallery-save" disabled={busy} onClick={saveProfile} className="self-start bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-5 py-2.5 flex items-center gap-2 transition-colors">
          <Save className="w-3.5 h-3.5" /> Save Profile
        </button>
      </motion.section>

      {/* Marketplace listings */}
      <motion.section className="border border-white/10 bg-black/40 p-6 flex flex-col gap-4" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.05, ease: EASE }}>
        <h3 className="font-sans font-bold uppercase text-base text-zinc-50 flex items-center gap-2"><Package className="w-5 h-5 text-nc-orange" /> My Marketplace Listings ({listings.length})</h3>
        {listings.map((l, i) => (
          <div key={l.id} data-testid={`my-listing-${i + 1}`} className="flex items-center justify-between gap-3 border border-white/10 bg-[#050505]/60 px-4 py-3">
            <div className="min-w-0">
              <p className="font-sans font-semibold text-sm text-zinc-100 truncate">{l.title}</p>
              <p className="font-mono text-[9px] text-zinc-500">{l.game_title ? `${l.game_title} // ` : ""}{ENGINES[l.engine]?.short} // {l.difficulty} // AUD {l.price.toFixed(2)}</p>
            </div>
            <button data-testid={`my-listing-delete-${i + 1}`} onClick={() => removeListing(l.id)} className="text-zinc-500 hover:text-red-400 transition-colors shrink-0"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}

        <div className="border border-nc-orange/30 bg-nc-orange/5 p-4 flex flex-col gap-3">
          <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase flex items-center gap-2"><Plus className="w-3.5 h-3.5" /> Create New Listing</p>
          <div>
            <label className={labelCls}>Division *</label>
            <div className="flex gap-2 flex-wrap">
              {MODDING_ENGINES.map((e) => (
                <button key={e} data-testid={`listing-engine-${e}`} onClick={() => setF("engine", e) || setForm((f) => ({ ...f, frameworks: [], compat_versions: [] }))} className={`border px-3 py-2 font-mono text-[9px] tracking-[0.15em] uppercase transition-colors ${form.engine === e ? ENGINES[e].accent : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
                  {ENGINES[e].label}
                </button>
              ))}
            </div>
          </div>
          {form.engine === "custom" && (
            <div>
              <label className={labelCls}>Which game? *</label>
              <input data-testid="listing-game-title" className={inputCls} placeholder="e.g. Rust, Garry's Mod, DayZ — any title" value={form.game_title} onChange={(e) => setF("game_title", e.target.value)} />
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input data-testid="listing-title" className={inputCls} placeholder="Listing title *" value={form.title} onChange={(e) => setF("title", e.target.value)} />
            <input data-testid="listing-price" type="number" min="1" className={inputCls} placeholder="Price (AUD) *" value={form.price} onChange={(e) => setF("price", e.target.value)} />          </div>
          <textarea data-testid="listing-description" rows={3} className={`${inputCls} resize-none`} placeholder="Description * (min 20 chars) — what it does, what is included" value={form.description} onChange={(e) => setF("description", e.target.value)} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {eng.frameworks.length > 0 && (
              <div>
                <label className={labelCls}>Required frameworks</label>
                <div className="flex gap-1.5 flex-wrap">
                  {eng.frameworks.map((f) => (
                    <button key={f} data-testid={`listing-fw-${f.replace(/[^A-Za-z]/g, "")}`} onClick={() => toggleForm("frameworks", f)} className={`border px-2.5 py-1.5 font-mono text-[9px] uppercase transition-colors ${form.frameworks.includes(f) ? "text-nc-orange border-nc-orange/50 bg-nc-orange/10" : "border-white/10 text-zinc-400"}`}>{f}</button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className={labelCls}>Installation difficulty</label>
              <div className="flex gap-1.5 flex-wrap">
                {DIFFICULTIES.map((d) => (
                  <button key={d} data-testid={`listing-diff-${d}`} onClick={() => setF("difficulty", d)} className={`border px-2.5 py-1.5 font-mono text-[9px] uppercase transition-colors ${form.difficulty === d ? "text-nc-blue border-nc-blue/50 bg-nc-blue/10" : "border-white/10 text-zinc-400"}`}>{d}</button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <label className={labelCls}>Compatibility matrix (engine versions)</label>
            <div className="flex gap-1.5 flex-wrap">
              {eng.versions.map((v) => (
                <button key={v} data-testid={`listing-ver-${v.replace(/[^A-Za-z0-9]/g, "")}`} onClick={() => toggleForm("compat_versions", v)} className={`border px-2.5 py-1.5 font-mono text-[9px] transition-colors ${form.compat_versions.includes(v) ? "text-nc-blue border-nc-blue/50 bg-nc-blue/10" : "border-white/10 text-zinc-400"}`}>{v}</button>
              ))}
            </div>
          </div>
          <input data-testid="listing-file" className={inputCls} placeholder="Source file URL (sealed until fund release) *" value={form.file_url} onChange={(e) => setF("file_url", e.target.value)} />
          <textarea data-testid="listing-patchnotes" rows={2} className={`${inputCls} resize-none`} placeholder="Patch notes * (mandatory)" value={form.patch_notes} onChange={(e) => setF("patch_notes", e.target.value)} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input data-testid="listing-video" className={inputCls} placeholder="Video walkthrough URL (optional)" value={form.video_url} onChange={(e) => setF("video_url", e.target.value)} />
            <input data-testid="listing-testserver" className={inputCls} placeholder="Join test server link (optional)" value={form.test_server} onChange={(e) => setF("test_server", e.target.value)} />
          </div>
          <button
            data-testid="listing-submit"
            disabled={busy || form.title.trim().length < 4 || form.description.trim().length < 20 || !(parseFloat(form.price) > 0) || form.file_url.trim().length < 5 || form.patch_notes.trim().length < 5 || (form.engine === "custom" && form.game_title.trim().length < 2)}
            onClick={createListing}
            className="self-start bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-5 py-2.5 transition-colors"
          >
            Publish Listing
          </button>
        </div>
      </motion.section>
    </div>
  );
}
